import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import MoneyInput from '../../common/MoneyInput';
import DefaultTermsFields from './DefaultTermsFields';
import IndividualApplicationQuotation from './IndividualApplicationQuotation';
import { IndividualPricingQuote, quoteMatches } from '../../../services/individualPricingQuotations';
import { addCalendarMonths, individualProductErrors, INDIVIDUAL_LOAN_PURPOSES } from '../individualProductRules';
import { pricingVersionLabel } from '../../../utils/pricingVersionLabel';

/** Individual product capture. Deliberately does not mount the company funding screen/calculator. */
export default function IndividualFundingStep({ showDefaultTerms = true, bureauRevision, showAffordability = true }: { showDefaultTerms?: boolean; bureauRevision?: string; showAffordability?: boolean } = {}) {
  const { register, control, watch, setValue, formState: { errors } } = useFormContext();
  const start = watch('loan.commencementDate');
  const payment = watch('loan.customLoanType');
  const due = watch('loan.dueDate');
  const loan = watch('loan');
  const quote = watch('loan.individualPricingQuote') as IndividualPricingQuote | undefined;
  const fieldErrors = (errors.loan || {}) as Record<string, any>;

  useEffect(() => {
    setValue('loan.securityType', 'UNSECURED');
    setValue('loan.securityAvailability', undefined);
    setValue('loan.securityTypesOffered', []);
    setValue('collaterals', []);
    // Compatibility with the current API; this is not a new pricing model.
    setValue('loan.loanProductType', 'TIRHELO');
    setValue('loan.calculationMode', 'CUSTOM');
    setValue('loan.fundingType', undefined);
  }, [setValue]);

  useEffect(() => {
    const days = (Date.parse(due) - Date.parse(start)) / 86400000;
    // Existing loan service uses inclusive days. Calendar end date governs the cap.
    setValue('loan.loanTerm', Number.isFinite(days) && days > 0 ? days + 1 : 0);
  }, [start, due, setValue]);

  const issue = (field: string) => fieldErrors[field]?.message ||
    individualProductErrors(loan).find(error => error.field === field)?.message;
  const error = (field: string) => fieldErrors[field] && <div role="alert" className="text-danger small">{issue(field)}</div>;
  return <section aria-labelledby="individual-funding-heading">
    <h5 id="individual-funding-heading">Individual loan request</h5>
    <p>Unsecured short-term loan. Maximum cash principal R8,000, excluding financed fees and VAT, repayable within six calendar months.</p>
    <div className="row g-3">
      <div className="col-md-6">
        <label htmlFor="individual-amount">Loan amount (R)</label>
        <Controller name="loan.loanAmount" control={control} render={({ field }) =>
          <MoneyInput id="individual-amount" step="0.01" max={8000} className="form-control"
            name={field.name} ref={field.ref} value={field.value} onValueChange={field.onChange} onBlur={field.onBlur} />} />{error('loanAmount')}
      </div>
      <div className="col-md-6">
        <label htmlFor="individual-start">Commencement date</label>
        <input id="individual-start" type="date" className="form-control" {...register('loan.commencementDate')} />{error('commencementDate')}
      </div>
      <div className="col-md-6">
        <label htmlFor="individual-payment">Payment type</label>
        <select id="individual-payment" className="form-select" {...register('loan.customLoanType')}>
          <option value="">Select payment type</option>
          <option value="ONCE_OFF">Single payment</option><option value="INSTALLMENTS">Monthly installments</option>
        </select>{error('customLoanType')}
      </div>
      {payment === 'INSTALLMENTS' && <div className="col-md-6">
        <label htmlFor="individual-installments">Number of monthly installments</label>
        <select id="individual-installments" className="form-select" {...register('loan.numberOfInstallments', {valueAsNumber: true})}>
          {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
        </select>{error('numberOfInstallments')}
      </div>}
      <div className="col-md-6">
        <label htmlFor="individual-due">Final repayment date</label>
        <input id="individual-due" type="date" className="form-control" readOnly
          max={start ? addCalendarMonths(start, 6) : undefined} {...register('loan.dueDate')} />{error('dueDate')}
        <small className="text-muted">Calculated automatically from the payday schedule and number of payments.</small>
      </div>
      <div className="col-12">
        <label htmlFor="individual-purpose">Loan purpose</label>
        <select id="individual-purpose" className="form-select" {...register('loan.loanPurpose')}>
          <option value="">[Please select a Purpose]</option>
          {INDIVIDUAL_LOAN_PURPOSES.map(purpose => <option key={purpose} value={purpose}>{purpose}</option>)}
        </select>{error('loanPurpose')}
        {loan?.loanPurpose && !INDIVIDUAL_LOAN_PURPOSES.includes(loan.loanPurpose) &&
          <p className="small text-warning">Previously captured purpose: {loan.loanPurpose}. Select a purpose from the list to continue.</p>}
      </div>
      <div className="col-12">
        <IndividualApplicationQuotation loan={loan} bureauRevision={bureauRevision} showAffordability={showAffordability} onUse={saved => {
          const input = saved.inputs, last = saved.instalments[saved.instalments.length - 1];
          setValue('loan.individualPricingQuote', saved);
          setValue('loan.loanAmount', Number(input.grossAdvance));
          setValue('loan.commencementDate', input.advanceDate);
          setValue('loan.numberOfInstallments', input.instalments);
          setValue('loan.customLoanType', input.instalments === 1 ? 'ONCE_OFF' : 'INSTALLMENTS');
          setValue('loan.dueDate', last.dueDate);
          const policy = saved.pricing.policy;
          setValue('loan.interestRate', Number(input.classification === 'FIRST' ? policy.firstMonthlyPercent : policy.subsequentMonthlyPercent));
          setValue('loan.interestAmount', Number(saved.totalInterest));
          setValue('loan.adminFee', Number(saved.scheduledRepayments) - Number(input.grossAdvance) - Number(saved.totalInterest));
          setValue('loan.totalRepayment', Number(saved.scheduledRepayments));
        }} />
        {quote && (quoteMatches(quote, loan) ? <>
          <p role="status">Quotation attached automatically: {pricingVersionLabel(quote.pricing)} · revision {quote.pricing.policy.revision}. Rates and charges come from this pricing.</p>
          <details><summary>Technical details</summary>Quotation ID: {quote.quoteId}<br />Pricing version ID: {quote.pricing.versionId}</details>
        </>
          : <p role="alert">The loan details changed. Waiting for the updated quotation.</p>)}
      </div>
      {showDefaultTerms && <DefaultTermsFields />}
    </div>
  </section>;
}
